// ═══ UTILITIES → OPEN QUESTIONS (Philip 2026-10-29, docs/open-questions-plan.md) ═══
// A shared list of data questions: any staff claims one, answers it (with a source) or releases it; the answerer can
// edit their answer; staff with "Can close questions" close and reopen. Status is derived (src/utils/openQuestions.js).
// ⛔ EVERY WRITE IS A DATABASE FUNCTION (question_claim / _release / _answer / _close / _reopen) that checks who is
//    asking and the question's state, and RAISES with the reason when it refuses — the table has no write policy at
//    all. Each call's returned row replaces the card; no row back is treated as a failure, never as success.
// ⛔ WHO MAY CLOSE IS ASKED OF THE DATABASE (can_close_questions(), the same check question_close makes) — the buttons
//    only mirror it.
// New questions come from scripts/import-questions.mjs, not from this page.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { TOPICS, STATUSES, questionStatus, sortQuestions } from '../utils/openQuestions';

const FONT = "'Public Sans',system-ui,sans-serif";
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const NO_DRAFT = { answer: '', source: '' };
const firstYear = (years) => (String(years || '').match(/\d{4}/) || [null])[0];

async function readAll(table, select) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from(table).select(select).order('id').range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}

export default function OpenQuestions({ darkMode, staffUser, onOpenParcel }) {
  const c = darkMode ? {
    card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68',
    soft: 'rgba(26,34,56,0.5)', link: '#9db8ff',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8',
    soft: 'var(--line-soft)', link: '#1d4ed8',
  };
  const CHIP = {
    open: { bg: darkMode ? '#3a4260' : '#EEF0F4', fg: darkMode ? '#d6dbea' : '#3f4656' },
    claimed: { bg: darkMode ? '#23406e' : '#E3ECFB', fg: darkMode ? '#cfe0ff' : '#1d4ed8' },
    answered: { bg: darkMode ? '#1f4a35' : '#E3F4EA', fg: darkMode ? '#bdebd0' : '#17663a' },
    closed: { bg: darkMode ? '#2b2f3a' : '#E6E6E9', fg: darkMode ? '#a8b0cc' : '#4a4a4f' },
  };
  const inp = { padding: '6px 9px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', fontFamily: FONT };
  const lbl = { fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' };
  const btn = { padding: '6px 12px', background: 'var(--red)', border: '1px solid var(--red)', borderRadius: '7px', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT };
  const btnGhost = { ...btn, background: 'transparent', border: `1px solid ${c.border}`, color: c.text };
  const chip = (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: bg, color: fg, whiteSpace: 'nowrap' });

  const me = staffUser?.id || null;
  const [rows, setRows] = useState([]);
  const [parcelsByQ, setParcelsByQ] = useState({});
  const [clients, setClients] = useState({});
  const [props, setProps] = useState({});
  const [staff, setStaff] = useState({});
  const [canClose, setCanClose] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [topic, setTopic] = useState('');
  const [openBg, setOpenBg] = useState({});
  const [drafts, setDrafts] = useState({});          // id → { answer, source } typed so far, or the answer being edited
  const [busy, setBusy] = useState(null);
  const [cardErr, setCardErr] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [qs, links] = await Promise.all([readAll('open_questions', '*'), readAll('open_question_parcels', 'id, question_id, position, label, parcel_id')]);
        const by = {};
        for (const l of links) (by[l.question_id] = by[l.question_id] || []).push(l);
        Object.values(by).forEach((l) => l.sort((a, b) => a.position - b.position));
        const propIds = [...new Set(qs.map((q) => q.property_id).filter(Boolean))];
        const [cl, pr, st] = await Promise.all([
          supabase.from('clients').select('id, name, short_name'),
          propIds.length ? supabase.from('properties').select('id, name').in('id', propIds) : Promise.resolve({ data: [] }),
          supabase.from('ntrg_staff').select('id, name'),
        ]);
        const bad = [cl, pr, st].find((r) => r.error);
        if (bad) throw bad.error;
        setRows(qs);
        setParcelsByQ(by);
        setClients(Object.fromEntries((cl.data || []).map((x) => [x.id, x])));
        setProps(Object.fromEntries((pr.data || []).map((x) => [x.id, x])));
        setStaff(Object.fromEntries((st.data || []).map((x) => [x.id, x.name])));
      } catch (e) {
        setErr(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    supabase.rpc('can_close_questions').then(({ data, error }) => setCanClose(!error && data === true), () => setCanClose(false));
  }, []);

  const call = async (q, fn, args = {}) => {
    setBusy(q.id);
    setCardErr((m) => ({ ...m, [q.id]: '' }));
    const { data, error } = await supabase.rpc(fn, { p_id: q.id, ...args });
    setBusy(null);
    if (error || !data || data.id !== q.id) {
      setCardErr((m) => ({ ...m, [q.id]: error?.message || 'Nothing was saved — try again.' }));
      return false;
    }
    setRows((rs) => rs.map((r) => (r.id === q.id ? data : r)));
    return true;
  };
  const saveAnswer = async (q) => {
    const d = drafts[q.id] || {};
    if (await call(q, 'question_answer', { p_answer: d.answer || '', p_source: d.source || '' })) setDrafts(({ [q.id]: _, ...rest }) => rest);
  };
  const startEdit = (q) => setDrafts((m) => ({ ...m, [q.id]: { answer: q.answer || '', source: q.source || '' } }));

  const mine = (q) => !!me && (q.claimed_by === me || q.answered_by === me);
  const propName = (q) => (q.property_id ? props[q.property_id]?.name : q.property_text) || '';
  const counts = useMemo(() => {
    const n = { all: rows.length, mine: rows.filter(mine).length, time_sensitive: rows.filter((q) => q.priority === 'time_sensitive').length };
    STATUSES.forEach((s) => { n[s.key] = rows.filter((q) => questionStatus(q) === s.key).length; });
    return n;
  }, [rows, me]);

  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return sortQuestions(rows.filter((q) => {
      if (filter === 'mine' && !mine(q)) return false;
      if (filter === 'time_sensitive' && q.priority !== 'time_sensitive') return false;
      if (STATUSES.some((x) => x.key === filter) && questionStatus(q) !== filter) return false;
      if (client && q.client_id !== client) return false;
      if (topic && q.topic !== topic) return false;
      if (!s) return true;
      const hay = [q.number, q.question, q.wigley_now, q.background, q.answer, q.source, q.years, propName(q),
        ...(parcelsByQ[q.id] || []).map((p) => p.label)].join(' ').toLowerCase();
      return hay.includes(s);
    }));
  }, [rows, filter, search, client, topic, parcelsByQ, props, me]);

  const clientOptions = [...new Set(rows.map((q) => q.client_id))].map((id) => clients[id]).filter(Boolean)
    .sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name));
  const tiles = [{ key: 'all', label: 'All' }, ...STATUSES, { key: 'mine', label: 'Mine' }, { key: 'time_sensitive', label: 'Time-sensitive' }];

  return (
    <div style={{ fontFamily: FONT, color: c.text }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>Open Questions</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Data questions for the team: claim one, answer it with a source, or release it.</div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {tiles.map((t) => {
          const on = filter === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setFilter(t.key)}
              style={{ minWidth: '96px', padding: '10px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: FONT, borderRadius: '10px',
                background: on ? (darkMode ? 'rgba(180,0,0,0.25)' : 'var(--red-tint)') : c.card, border: `1.5px solid ${on ? 'var(--red)' : c.border}`, color: c.text }}>
              <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: "'Archivo',sans-serif", color: t.key === 'time_sensitive' && counts[t.key] ? 'var(--red)' : c.text }}>{counts[t.key] ?? 0}</div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: c.muted }}>{t.label}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px' }}>
        <div><div style={lbl}>Search</div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Question, parcel, answer…" style={{ ...inp, width: '240px' }} /></div>
        <div><div style={lbl}>Client</div>
          <select value={client} onChange={(e) => setClient(e.target.value)} style={inp}>
            <option value="">All clients</option>
            {clientOptions.map((x) => <option key={x.id} value={x.id}>{x.short_name || x.name}</option>)}
          </select></div>
        <div><div style={lbl}>Topic</div>
          <select value={topic} onChange={(e) => setTopic(e.target.value)} style={inp}>
            <option value="">All topics</option>
            {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: '12px', marginBottom: '10px' }}>Could not load the questions: {err}</div>}
      {loading && <div style={{ color: c.muted, fontSize: '13px' }}>Loading…</div>}
      {!loading && !err && !shown.length && (
        <div style={{ padding: '28px', textAlign: 'center', color: c.muted, fontSize: '13px', background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px' }}>No questions for these filters.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {shown.map((q) => {
          const st = questionStatus(q);
          const cl = clients[q.client_id];
          const links = parcelsByQ[q.id] || [];
          const claimedByMe = st === 'claimed' && q.claimed_by === me;
          const draft = drafts[q.id] || (claimedByMe ? NO_DRAFT : null);   // claimed by me: the answer box is open
          const year = firstYear(q.years);
          const isBusy = busy === q.id;
          return (
            <div key={q.id} style={{ background: c.card, border: `1px solid ${q.priority === 'time_sensitive' && st !== 'closed' ? 'var(--red)' : c.border}`, borderRadius: '12px', padding: '14px 16px', opacity: st === 'closed' ? 0.8 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: '700', fontSize: '15px' }}>#{q.number}</span>
                <span style={{ fontSize: '12px', color: c.muted }}>
                  {[cl?.short_name || cl?.name, propName(q)].filter(Boolean).join(' · ')}
                  {links.length > 0 && ' · '}
                  {links.map((l, i) => (
                    <React.Fragment key={l.id}>
                      {i > 0 && ' / '}
                      {l.parcel_id
                        ? <button type="button" onClick={() => onOpenParcel(l.parcel_id, year)} title="Open the parcel card"
                            style={{ background: 'none', border: 'none', padding: 0, color: c.link, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>{l.label}</button>
                        : <span>{l.label}</span>}
                    </React.Fragment>
                  ))}
                  {q.years && ` · ${q.years}`}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: '11px', color: c.muted }}>{q.topic}</span>
                {q.priority === 'time_sensitive' && <span style={chip(darkMode ? 'rgba(180,0,0,0.35)' : 'var(--red-tint)', darkMode ? '#ffd0d0' : 'var(--red-dark)')}>Time-sensitive</span>}
                <span style={chip(CHIP[st].bg, CHIP[st].fg)}>{STATUSES.find((s) => s.key === st).label}</span>
              </div>

              <div style={{ fontSize: '14px', fontWeight: '600', margin: '8px 0 6px', lineHeight: 1.45 }}>{q.question}</div>
              {q.wigley_now && <div style={{ fontSize: '12.5px', lineHeight: 1.45 }}><span style={{ fontWeight: '700', color: c.muted }}>Wigley now: </span>{q.wigley_now}</div>}
              {q.background && (
                <div style={{ marginTop: '6px' }}>
                  <button type="button" onClick={() => setOpenBg((m) => ({ ...m, [q.id]: !m[q.id] }))}
                    style={{ background: 'none', border: 'none', padding: 0, color: c.muted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT }}>
                    {openBg[q.id] ? '▾' : '▸'} Background
                  </button>
                  {openBg[q.id] && <div style={{ fontSize: '12.5px', lineHeight: 1.45, marginTop: '4px', padding: '8px 10px', background: c.soft, borderRadius: '8px' }}>{q.background}</div>}
                </div>
              )}

              {q.answer != null && !draft && (
                <div style={{ marginTop: '10px', padding: '10px 12px', borderLeft: '3px solid #17663a', background: c.soft, borderRadius: '6px' }}>
                  <div style={{ fontSize: '13px', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{q.answer}</div>
                  <div style={{ fontSize: '11px', color: c.muted, marginTop: '6px' }}>
                    {q.source && <>Source: {q.source} · </>}Answered by {staff[q.answered_by] || '—'} · {fmtDate(q.answered_at)}
                  </div>
                </div>
              )}

              {draft && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <textarea value={draft.answer} onChange={(e) => setDrafts((m) => ({ ...m, [q.id]: { ...(m[q.id] || NO_DRAFT), answer: e.target.value } }))}
                    placeholder="Answer" rows={3} style={{ ...inp, fontSize: '13px', resize: 'vertical' }} />
                  <input value={draft.source} onChange={(e) => setDrafts((m) => ({ ...m, [q.id]: { ...(m[q.id] || NO_DRAFT), source: e.target.value } }))}
                    placeholder="Source (e.g. Debbie 10/29, county website, bill PDF)" style={inp} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
                <span style={{ fontSize: '11px', color: c.muted, flex: 1 }}>
                  {st === 'claimed' && `Claimed by ${q.claimed_by === me ? 'you' : staff[q.claimed_by] || '—'} · ${fmtDate(q.claimed_at)}`}
                  {st === 'closed' && `Closed by ${staff[q.closed_by] || '—'} · ${fmtDate(q.closed_at)}`}
                </span>
                {st === 'open' && <button type="button" disabled={isBusy} style={btn} onClick={() => call(q, 'question_claim')}>Claim</button>}
                {draft && <button type="button" disabled={isBusy || !draft.answer.trim()} style={{ ...btn, opacity: draft.answer.trim() ? 1 : 0.5 }} onClick={() => saveAnswer(q)}>Save answer</button>}
                {draft && q.answer != null && <button type="button" disabled={isBusy} style={btnGhost} onClick={() => setDrafts(({ [q.id]: _, ...rest }) => rest)}>Cancel</button>}
                {claimedByMe && <button type="button" disabled={isBusy} style={btnGhost} onClick={() => call(q, 'question_release').then((ok) => ok && setDrafts(({ [q.id]: _, ...rest }) => rest))}>Release</button>}
                {st === 'answered' && q.answered_by === me && !draft && <button type="button" disabled={isBusy} style={btnGhost} onClick={() => startEdit(q)}>Edit answer</button>}
                {canClose && st !== 'closed' && <button type="button" disabled={isBusy} style={btnGhost} onClick={() => call(q, 'question_close')}>Close</button>}
                {canClose && st === 'closed' && <button type="button" disabled={isBusy} style={btnGhost} onClick={() => call(q, 'question_reopen')}>Reopen</button>}
              </div>
              {cardErr[q.id] && <div style={{ color: 'var(--red)', fontSize: '12px', marginTop: '6px' }}>{cardErr[q.id]}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
